// src/components/Aufgaben.js
import React, { useMemo, useState, useEffect } from 'react';
import './Aufgaben.css';
import './components.css';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import de from 'date-fns/locale/de';
import * as XLSX from 'xlsx-js-style';
import {
  autoReconnectDirectory,
  hasSelectedDirectory,
  selectDirectory,
  saveFileToDirectory,
  listFilesInDirectory,
  loadFileFromDirectory
} from '../utils/fileSystem';
import { useUser } from '../UserContext';

const locales = { de };
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }),
  getDay,
  locales,
});

// Schlüssel für localStorage
const STORAGE_KEY = 'aufgabenByDate_v1';
const CATEGORY = 'Aufgaben';

function Aufgaben() {
  const { user: currentUser } = useUser();
  const [selectedDate, setSelectedDate] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [aufgabenByDate, setAufgabenByDate] = useState({});
  const [isDirectorySelected, setIsDirectorySelected] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  // Eingabestates für neue Aufgaben
  const [newTagText, setNewTagText] = useState('');
  const [newTagUser, setNewTagUser] = useState('');
  const [newNachtText, setNewNachtText] = useState('');
  const [newNachtUser, setNewNachtUser] = useState('');

  // Initialisierung: Speicherort prüfen und ggf. aus Excel laden, sonst aus localStorage
  useEffect(() => {
    const init = async () => {
      try {
        const hasDir = await hasSelectedDirectory(CATEGORY);
        setIsDirectorySelected(!!hasDir);
        if (hasDir) {
          const reconnected = await autoReconnectDirectory(CATEGORY);
          if (reconnected) {
            await loadFromExcel();
            return;
          }
        }
        // Fallback: localStorage
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) {
          const parsed = JSON.parse(raw);
          setAufgabenByDate(parsed || {});
        }
      } catch (e) {
        console.error('Aufgaben init fehlgeschlagen:', e);
      }
    };
    init();
  }, []);

  // Speichern in localStorage
  const persist = (data) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (e) {
      console.error('Aufgaben speichern fehlgeschlagen:', e);
    }
  };

  // Bei Änderungen: localStorage aktualisieren und, wenn Ordner ausgewählt, Excel speichern
  useEffect(() => {
    persist(aufgabenByDate);
    const doSave = async () => {
      try {
        if (isDirectorySelected) {
          await saveToExcel();
          setMessage({ text: 'Aufgaben gespeichert.', type: 'success' });
        }
      } catch (e) {
        console.error('Excel speichern fehlgeschlagen:', e);
        setMessage({ text: 'Speichern fehlgeschlagen.', type: 'error' });
      }
    };
    if (Object.keys(aufgabenByDate).length > 0) doSave();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aufgabenByDate]);

  const handleDirectorySelect = async () => {
    try {
      const dirName = await selectDirectory(CATEGORY);
      if (dirName) {
        setIsDirectorySelected(true);
        setMessage({ text: `Speicherort ausgewählt: ${dirName}`, type: 'success' });
        await loadFromExcel();
      }
    } catch (error) {
      console.error('Fehler bei der Verzeichnisauswahl:', error);
      setMessage({ text: `Fehler bei der Verzeichnisauswahl: ${error.message}`, type: 'error' });
    }
  };

  const loadFromExcel = async () => {
    try {
      const files = await listFilesInDirectory(CATEGORY, 'Aufgaben', '.xlsx');
      if (files.length === 0) {
        return; // nichts zu laden
      }
      const latest = files[0];
      const file = await loadFileFromDirectory(CATEGORY, latest.name);
      if (!file) return;
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws);
      // Re-Konstruktion der Struktur
      const next = {};
      rows.forEach(r => {
        const key = r.Datum;
        const schicht = (r.Schicht || '').toLowerCase(); // 'tag' | 'nacht'
        if (!key || (schicht !== 'tag' && schicht !== 'nacht')) return;
        if (!next[key]) next[key] = { tag: [], nacht: [] };
        next[key][schicht].push({
          text: r.Aufgabe || '',
          user: r.ZugewiesenAn || '',
          createdAt: r.ErstelltAm || new Date().toISOString(),
          status: r.Status || 'offen',
          reason: r.Grund || '',
        });
      });
      setAufgabenByDate(next);
    } catch (e) {
      console.error('Excel laden fehlgeschlagen:', e);
      setMessage({ text: 'Excel laden fehlgeschlagen.', type: 'error' });
    }
  };

  const saveToExcel = async () => {
    // Flache Zeilenstruktur: Datum, Schicht, Aufgabe, ZugewiesenAn, ErstelltAm, Status, Grund
    const rows = [];
    Object.entries(aufgabenByDate).forEach(([key, val]) => {
      (val.tag || []).forEach(t => {
        rows.push({
          Datum: key,
          Schicht: 'tag',
          Aufgabe: t.text,
          ZugewiesenAn: t.user || '',
          ErstelltAm: t.createdAt,
          Status: t.status || 'offen',
          Grund: t.reason || '',
        });
      });
      (val.nacht || []).forEach(t => {
        rows.push({
          Datum: key,
          Schicht: 'nacht',
          Aufgabe: t.text,
          ZugewiesenAn: t.user || '',
          ErstelltAm: t.createdAt,
          Status: t.status || 'offen',
          Grund: t.reason || '',
        });
      });
    });

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = [
      { wch: 12 }, // Datum
      { wch: 8 },  // Schicht
      { wch: 50 }, // Aufgabe
      { wch: 20 }, // ZugewiesenAn
      { wch: 20 }, // ErstelltAm
      { wch: 12 }, // Status
      { wch: 30 }, // Grund
    ];
    XLSX.utils.book_append_sheet(wb, ws, 'Aufgaben');
    const buffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const ts = format(new Date(), 'yyyy-MM-dd');
    const fileName = `Aufgaben_${ts}.xlsx`;
    await saveFileToDirectory(CATEGORY, fileName, buffer);
  };

  const handleSelectSlot = ({ start }) => {
    setSelectedDate(start);
    setShowModal(true);
  };

  const formatKey = (date) => format(date, 'yyyy-MM-dd');

  const dayData = useMemo(() => {
    if (!selectedDate) return { tag: [], nacht: [] };
    const key = formatKey(selectedDate);
    return aufgabenByDate[key] || { tag: [], nacht: [] };
  }, [selectedDate, aufgabenByDate]);

  // Heutige Aufgaben direkt anzeigen
  const today = useMemo(() => new Date(), []);
  const todayKey = useMemo(() => formatKey(today), [today]);
  const todayData = useMemo(() => {
    return aufgabenByDate[todayKey] || { tag: [], nacht: [] };
  }, [aufgabenByDate, todayKey]);

  const openTodayEditor = () => {
    setSelectedDate(today);
    setShowModal(true);
  };

  const addTagAufgabe = () => {
    if (!newTagText.trim()) return;
    const key = formatKey(selectedDate);
    const next = {
      ...aufgabenByDate,
      [key]: {
        tag: [
          ...(aufgabenByDate[key]?.tag || []),
          { text: newTagText.trim(), user: newTagUser.trim(), createdAt: new Date().toISOString(), status: 'offen', reason: '' }
        ],
        nacht: [...(aufgabenByDate[key]?.nacht || [])],
      },
    };
    setAufgabenByDate(next);
    persist(next);
    setNewTagText('');
    setNewTagUser('');
  };

  const addNachtAufgabe = () => {
    if (!newNachtText.trim()) return;
    const key = formatKey(selectedDate);
    const next = {
      ...aufgabenByDate,
      [key]: {
        tag: [...(aufgabenByDate[key]?.tag || [])],
        nacht: [
          ...(aufgabenByDate[key]?.nacht || []),
          { text: newNachtText.trim(), user: newNachtUser.trim(), createdAt: new Date().toISOString(), status: 'offen', reason: '' }
        ],
      },
    };
    setAufgabenByDate(next);
    persist(next);
    setNewNachtText('');
    setNewNachtUser('');
  };

  const removeTask = (type, index) => {
    if (!selectedDate) return;
    const key = formatKey(selectedDate);
    const current = aufgabenByDate[key] || { tag: [], nacht: [] };
    const nextList = [...current[type]];
    nextList.splice(index, 1);
    const next = { ...aufgabenByDate, [key]: { ...current, [type]: nextList } };
    setAufgabenByDate(next);
    persist(next);
  };

  // Aktualisiert Felder (Status/Grund/…) einer Aufgabe
  const updateTask = (type, index, updates) => {
    if (!selectedDate) return;
    const key = formatKey(selectedDate);
    const current = aufgabenByDate[key] || { tag: [], nacht: [] };
    const list = [...current[type]];
    list[index] = { ...list[index], ...updates };
    const next = { ...aufgabenByDate, [key]: { ...current, [type]: list } };
    setAufgabenByDate(next);
    persist(next);
  };

  // Für die Monatsansicht zeigen wir Indikatoren je Tag an, ob Aufgaben existieren
  const events = useMemo(() => {
    const result = [];
    Object.entries(aufgabenByDate).forEach(([key, val]) => {
      const [y, m, d] = key.split('-').map(Number);
      const day = new Date(y, (m - 1), d);
      const count = (val.tag?.length || 0) + (val.nacht?.length || 0);
      if (count > 0) {
        result.push({
          title: `${count} Aufgaben`,
          start: day,
          end: day,
          allDay: true,
        });
      }
    });
    return result;
  }, [aufgabenByDate]);

  const canDelete = useMemo(() => {
    const role = currentUser?.role?.toLowerCase?.() || '';
    return role === 'superadmin' || role === 'admin';
  }, [currentUser]);

  return (
    <div className="aufgaben-container">
      <div className="dienstuebergabe-header">
        <h1>Aufgaben</h1>
        <div className="header-actions">
          {!isDirectorySelected ? (
            <button className="btn-secondary" onClick={handleDirectorySelect}>
              <i className="fas fa-folder-open"></i> Speicherort auswählen
            </button>
          ) : null}
        </div>
      </div>

      {message.text && (
        <div className={`message message-${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="content-card" style={{ marginBottom: 20 }}>
        <div className="heute-header">
          <h2 style={{ margin: 0 }}>Heutige Aufgaben ({format(today, 'dd.MM.yyyy')})</h2>
          <button className="btn-primary" onClick={openTodayEditor}>
            Bearbeiten
          </button>
        </div>
        <div className="heute-grid">
          <div className="heute-col">
            <h4>Tagdienst</h4>
            {todayData.tag.length === 0 ? (
              <div className="heute-empty">Keine Aufgaben</div>
            ) : (
              <ul className="aufgaben-list">
                {todayData.tag.map((t, idx) => (
                  <li key={`heute-tag-${idx}`}>
                    <div className="task-main">
                      <span className="task-text">{t.text}</span>
                      {t.user && <span className="task-user">{t.user}</span>}
                    </div>
                    <div className="task-meta">erstellt: {format(new Date(t.createdAt), 'dd.MM.yyyy HH:mm')}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="heute-col">
            <h4>Nachtdienst</h4>
            {todayData.nacht.length === 0 ? (
              <div className="heute-empty">Keine Aufgaben</div>
            ) : (
              <ul className="aufgaben-list">
                {todayData.nacht.map((t, idx) => (
                  <li key={`heute-nacht-${idx}`}>
                    <div className="task-main">
                      <span className="task-text">{t.text}</span>
                      {t.user && <span className="task-user">{t.user}</span>}
                    </div>
                    <div className="task-meta">erstellt: {format(new Date(t.createdAt), 'dd.MM.yyyy HH:mm')}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      <div className="content-card">
        <div className="aufgaben-calendar">
          <Calendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            views={['month']}
            style={{ height: 600 }}
            selectable
            onSelectSlot={handleSelectSlot}
            onSelectEvent={({ start }) => handleSelectSlot({ start })}
            messages={{
              next: 'Weiter',
              previous: 'Zurück',
              today: 'Heute',
              month: 'Monat',
            }}
            culture="de"
          />
        </div>
      </div>

      {showModal && selectedDate && (
        <div className="aufgaben-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="aufgaben-modal" onClick={(e) => e.stopPropagation()}>
            <div className="aufgaben-modal-header">
              <h3>Aufgaben für den {format(selectedDate, 'dd.MM.yyyy')}</h3>
              <button className="close-btn" onClick={() => setShowModal(false)}>×</button>
            </div>
            <div className="aufgaben-modal-body">
              <div className="aufgaben-col">
                <h4>Tagdienst</h4>
                <div className="aufgaben-add-row">
                  <input
                    type="text"
                    placeholder="Aufgabe"
                    value={newTagText}
                    onChange={(e) => setNewTagText(e.target.value)}
                  />
                  <input
                    type="text"
                    placeholder="zugewiesen an (optional)"
                    value={newTagUser}
                    onChange={(e) => setNewTagUser(e.target.value)}
                  />
                  <button className="btn-primary" onClick={addTagAufgabe}>Hinzufügen</button>
                </div>
                <ul className="aufgaben-list">
                  {dayData.tag.map((t, idx) => (
                    <li key={`tag-${idx}`}>
                      <div className="task-main">
                        <span className="task-text">{t.text}</span>
                        {t.user && <span className="task-user">{t.user}</span>}
                      </div>
                      <div className="task-meta">erstellt: {format(new Date(t.createdAt), 'dd.MM.yyyy HH:mm')}</div>
                      <div className="aufgaben-status-row">
                        <select
                          value={t.status || 'offen'}
                          onChange={(e) => updateTask('tag', idx, { status: e.target.value })}
                        >
                          <option value="offen">Offen</option>
                          <option value="erledigt">Erledigt</option>
                          <option value="nicht">Nicht erledigt</option>
                          <option value="teilweise">Teilweise</option>
                        </select>
                        {(t.status === 'nicht' || t.status === 'teilweise') && (
                          <input
                            type="text"
                            placeholder="Grund (Pflicht)"
                            value={t.reason || ''}
                            onChange={(e) => updateTask('tag', idx, { reason: e.target.value })}
                            required
                          />
                        )}
                        {canDelete && (
                          <button className="delete-btn" onClick={() => removeTask('tag', idx)}>Löschen</button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="aufgaben-col">
                <h4>Nachtdienst</h4>
                <div className="aufgaben-add-row">
                  <input
                    type="text"
                    placeholder="Aufgabe"
                    value={newNachtText}
                    onChange={(e) => setNewNachtText(e.target.value)}
                  />
                  <input
                    type="text"
                    placeholder="zugewiesen an (optional)"
                    value={newNachtUser}
                    onChange={(e) => setNewNachtUser(e.target.value)}
                  />
                  <button className="btn-primary" onClick={addNachtAufgabe}>Hinzufügen</button>
                </div>
                <ul className="aufgaben-list">
                  {dayData.nacht.map((t, idx) => (
                    <li key={`nacht-${idx}`}>
                      <div className="task-main">
                        <span className="task-text">{t.text}</span>
                        {t.user && <span className="task-user">{t.user}</span>}
                      </div>
                      <div className="task-meta">erstellt: {format(new Date(t.createdAt), 'dd.MM.yyyy HH:mm')}</div>
                      <div className="aufgaben-status-row">
                        <select
                          value={t.status || 'offen'}
                          onChange={(e) => updateTask('nacht', idx, { status: e.target.value })}
                        >
                          <option value="offen">Offen</option>
                          <option value="erledigt">Erledigt</option>
                          <option value="nicht">Nicht erledigt</option>
                          <option value="teilweise">Teilweise</option>
                        </select>
                        {(t.status === 'nicht' || t.status === 'teilweise') && (
                          <input
                            type="text"
                            placeholder="Grund (Pflicht)"
                            value={t.reason || ''}
                            onChange={(e) => updateTask('nacht', idx, { reason: e.target.value })}
                            required
                          />
                        )}
                        {canDelete && (
                          <button className="delete-btn" onClick={() => removeTask('nacht', idx)}>Löschen</button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Aufgaben;
